import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Popper,
  TextField,
  Button,
  Typography,
  IconButton,
  Chip,
  Fade,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import {
  pdfToScreen,
  pdfToScreenLegacy,
  screenToPDF,
  applyZoom,
  isValidCoordinates,
  getDefaultPDFDimensions,
  type Coordinates,
  type PDFDimensions
} from '../../../utils/coordinateTransform';

interface Annotation {
  id: string;
  type: string;
  priority: string;
  field_name: string;
  message: string;
  coordinates: { x: number; y: number; width: number; height: number };
  page_number: number;
  created_by: string;
  created_at: string;
  resolved: boolean;
  replies: any[];
}

interface AnnotationLayerProps {
  annotations: Annotation[];
  selectedAnnotation: Annotation | null;
  onAnnotationClick: (annotation: Annotation) => void;
  onAddAnnotation: (annotation: any) => void;
  annotationMode: string;
  pdfDimensions?: PDFDimensions;
  currentZoom?: number;
}

const AnnotationLayer: React.FC<AnnotationLayerProps> = ({
  annotations,
  selectedAnnotation,
  onAnnotationClick,
  onAddAnnotation,
  annotationMode,
  pdfDimensions = getDefaultPDFDimensions(),
  currentZoom = 1.0,
}) => {
  const theme = useTheme();
  const [isCreating, setIsCreating] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [annotationText, setAnnotationText] = useState('');
  const layerRef = useRef<HTMLDivElement>(null);

  const handleLayerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (annotationMode === 'view') return;

    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Get screen coordinates
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;

    // Convert to PDF coordinates for storage
    const pdfCoords = screenToPDF(
      { x: screenX, y: screenY, width: 100, height: 30, page: 1 },
      pdfDimensions
    );

    setNewAnnotation({
      coordinates: pdfCoords,  // Store in PDF coordinates
      type: annotationMode,
      page_number: pdfCoords.page,
    });

    setAnchorEl(event.currentTarget);
    setIsCreating(true);
  };

  const handleCreateAnnotation = () => {
    if (!annotationText.trim()) return;

    onAddAnnotation({
      ...newAnnotation,
      message: annotationText,
      field_name: `Field_${Date.now()}`,
      priority: annotationMode === 'highlight' ? 'high' : 'medium',
    });

    setIsCreating(false);
    setAnnotationText('');
    setNewAnnotation(null);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setAnnotationText('');
    setNewAnnotation(null);
    setAnchorEl(null);
  };

  const getAnnotationStyle = (annotation: Annotation) => {
    // Transform coordinates from PDF space to screen space
    const coords: Coordinates = {
      x: annotation.coordinates.x,
      y: annotation.coordinates.y,
      width: annotation.coordinates.width,
      height: annotation.coordinates.height,
      page: annotation.page_number
    };

    const PAGE_GAP = 20; // Gap between pages in pixels
    const PDF_SIDEBAR_OFFSET = 220; // Offset for browser PDF viewer's left sidebar
    const screenCoords = pdfToScreenLegacy(coords, pdfDimensions, PAGE_GAP);
    // Add PDF sidebar offset to avoid thumbnails
    screenCoords.x += PDF_SIDEBAR_OFFSET;
    const zoomedCoords = applyZoom(screenCoords, currentZoom);

    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${zoomedCoords.x}px`,
      top: `${zoomedCoords.y}px`,
      width: `${zoomedCoords.width}px`,
      height: `${zoomedCoords.height}px`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      borderRadius: '4px',
    };

    switch (annotation.type) {
      case 'highlight':
      case 'critical':
        return {
          ...baseStyle,
          backgroundColor: annotation.priority === 'critical' 
            ? 'rgba(244, 67, 54, 0.3)' 
            : 'rgba(255, 235, 59, 0.3)',
          border: '2px solid',
          borderColor: annotation.priority === 'critical' ? '#f44336' : '#ffeb3b',
        };
      case 'comment':
        return {
          ...baseStyle,
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          border: '2px dashed #2196f3',
        };
      case 'issue':
      case 'compliance':
        return {
          ...baseStyle,
          backgroundColor: 'rgba(255, 152, 0, 0.2)',
          border: '2px solid #ff9800',
        };
      case 'missing':
        return {
          ...baseStyle,
          backgroundColor: 'rgba(158, 158, 158, 0.2)',
          border: '2px dashed #9e9e9e',
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          border: '1px solid #000',
        };
    }
  };

  const getAnnotationLabel = (annotation: Annotation) => {
    const labels: { [key: string]: string } = {
      critical: 'Critical',
      highlight: 'Important',
      comment: 'Note',
      issue: 'Issue',
      compliance: 'Compliance',
      missing: 'Missing',
      workflow: 'Workflow',
    };
    return labels[annotation.type] || annotation.type;
  };

  return (
    <Box
      ref={layerRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: annotationMode !== 'view' ? 'auto' : 'none',
      }}
      onClick={handleLayerClick}
    >
      {/* Render existing annotations */}
      {annotations.map((annotation) => (
        <Box
          key={annotation.id}
          style={getAnnotationStyle(annotation)}
          onClick={(e) => {
            e.stopPropagation();
            onAnnotationClick(annotation);
          }}
          sx={{
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.05)',
            },
          }}
        >
          {/* Annotation marker */}
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              left: -8,
              bgcolor: 'background.paper',
              borderRadius: '50%',
              border: 2,
              borderColor: annotation.resolved ? theme.palette.success.main : theme.palette.primary.main,
              width: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {annotation.resolved && (
              <CheckIcon sx={{ fontSize: 10, color: theme.palette.success.main }} />
            )}
          </Box>

          {/* Annotation label */}
          <Chip
            label={getAnnotationLabel(annotation)}
            size="small"
            sx={{
              position: 'absolute',
              top: -25,
              left: 0,
              height: 18,
              fontSize: '0.7rem',
            }}
            color={
              annotation.priority === 'critical'
                ? 'error'
                : annotation.priority === 'high'
                ? 'warning'
                : 'default'
            }
          />

          {/* Reply count badge */}
          {annotation.replies && annotation.replies.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -8,
                right: -8,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
              }}
            >
              {annotation.replies.length}
            </Box>
          )}
        </Box>
      ))}

      {/* New annotation creator */}
      <Popper
        open={isCreating}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper sx={{ p: 2, mt: 1, minWidth: 300 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Add {annotationMode === 'highlight' ? 'Highlight' : 'Comment'}
                </Typography>
                <IconButton size="small" onClick={handleCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Enter annotation text..."
                value={annotationText}
                onChange={(e) => setAnnotationText(e.target.value)}
                autoFocus
                size="small"
                sx={{ mb: 1 }}
              />

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleCreateAnnotation}
                  disabled={!annotationText.trim()}
                >
                  Add
                </Button>
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>

      {/* Selected annotation details */}
      {selectedAnnotation && (
        <Popper
          open={Boolean(selectedAnnotation)}
          anchorEl={
            document.querySelector(`[data-annotation-id="${selectedAnnotation.id}"]`)
          }
          placement="right"
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper sx={{ p: 2, ml: 2, maxWidth: 300 }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedAnnotation.field_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedAnnotation.message}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  <Chip
                    label={selectedAnnotation.type}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={selectedAnnotation.priority}
                    size="small"
                    color={
                      selectedAnnotation.priority === 'critical'
                        ? 'error'
                        : selectedAnnotation.priority === 'high'
                        ? 'warning'
                        : 'default'
                    }
                  />
                </Box>

                <Typography variant="caption" color="text.secondary">
                  By {selectedAnnotation.created_by} •{' '}
                  {new Date(selectedAnnotation.created_at).toLocaleDateString()}
                </Typography>

                {/* Replies */}
                {selectedAnnotation.replies && selectedAnnotation.replies.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Replies ({selectedAnnotation.replies.length})
                    </Typography>
                    {selectedAnnotation.replies.slice(-2).map((reply: any) => (
                      <Box key={reply.id} sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {reply.user_name}
                        </Typography>
                        <Typography variant="body2">{reply.message}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button size="small" startIcon={<ReplyIcon />}>
                    Reply
                  </Button>
                  {!selectedAnnotation.resolved && (
                    <Button size="small" startIcon={<CheckIcon />} color="success">
                      Resolve
                    </Button>
                  )}
                </Box>
              </Paper>
            </Fade>
          )}
        </Popper>
      )}
    </Box>
  );
};

export default AnnotationLayer;